<template>
  <ContainerBasic
    container-type="FULL_WIDTH"
    class="px-0 !mt-3xl scroll-to-form"
  >
    <ClientOnly>
      <div ref="bannerNode">
        <div
          class="touch:md:h-[30px] lg:h-[30px] h-[20px]"
          style="background-color: #628e92"
        />
        <div
          class="min-h-[329px] bg-left-bottom bg-cover relative"
          :style="`background-image: url(${imageUrl})`"
        >
          <ContainerBasic container-type="PAGE">
            <div class="relative">
              <div
                class="absolute right-1/2 touch:md:right-0 lg:right-0 translate-x-1/2 touch:md:translate-x-0 touch:md:-translate-y-[52.95%] lg:translate-x-0 lg:-translate-y-[52.95%] -translate-y-[85.95%] w-full touch:md:w-auto lg:w-auto"
              >
                <div class="relative flex items-center justify-center">
                  <img
                    v-if="!isDesktopView"
                    src="@/assets/images/icon_badge_mobile.svg"
                    class="mx-auto"
                    loading="lazy"
                  />
                  <img
                    v-if="isDesktopView"
                    src="@/assets/images/icon_badge.svg"
                    class="mx-auto"
                    loading="lazy"
                  />
                  <div ref="text" class="absolute text-center">
                    <p class="text-[32px] font-bold">100%</p>
                    <p>{{ t('event.forFree') }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div
              class="flex flex-col px-0 touch:md:flex-row touch:md:px-sm lg:flex-row lg:px-sm pb-4xl touch:md:pb-xl lg:pb-xl pt-3xl touch:md:pt-xl lg:pt-xl"
            >
              <div
                class="basis-full touch:md:basis-3/4 lg:basis-3/4 px-md touch:md:pl-0 touch:md:pr-lg lg:pl-0 lg:pr-lg"
              >
                <Headline
                  classes="font-josefin text-[24px] touch:md:text-[40px] lg:text-[40px] font-bold"
                  align-text="left"
                  :headline="blockData.headline"
                  level="h2"
                  max-width=""
                />

                <WYSIWYGContentRaw
                  class="mt-sm"
                  :content="blockData.text"
                  inner-classes="!ml-0 px-0"
                ></WYSIWYGContentRaw>

                <div
                  v-if="blockData.paywallCheckboxes.length"
                  class="flex flex-row flex-wrap mt-md"
                >
                  <div
                    v-for="checkbox in blockData.paywallCheckboxes"
                    :key="checkbox.value"
                    class="basis-full md:basis-1/2 text-[27px] font-josefin flex items-center min-w-0"
                  >
                    <img
                      src="@/assets/images/checkbox_checked.svg"
                      loading="lazy"
                    />
                    <p class="min-w-0 break-words">{{ checkbox.label }}</p>
                  </div>
                </div>
              </div>
              <div
                class="basis-full touch:md:basis-1/4 lg:basis-1/4 mt-md touch:md:mt-xl lg:mt-xl px-md touch:md:pl-sm touch:md:pr-0 lg:pl-sm lg:pr-0"
              >
                <FormKit
                  id="paywallForm"
                  type="form"
                  class="max-w-md"
                  spellcheck="false"
                  :actions="false"
                  :incomplete-message="false"
                  :config="{ validationVisibility: 'blur' }"
                  @submit="onSubmit"
                  @node="watchForm"
                >
                  <FormKit
                    type="text"
                    name="EMAIL"
                    label="Ihre E-Mail-Adresse"
                    placeholder="max@mustermann.de"
                    validation="required|email"
                    validation-visibility="blur"
                    input-class="w-full input"
                    inner-class="relative pt-[5px]"
                    label-class="text-md !leading-[19px] font-bold"
                  />

                  <!-- real people should not fill this in and expect good things - do not remove this or risk form bot signups-->
                  <FormKit
                    type="hidden"
                    name="b_a8780ef3529534dbe1b892a2a_da0d21a119"
                    value=""
                  />

                  <ButtonBasic
                    classes="w-full my-sm px-0"
                    label="Checklisten anfordern"
                    bg-color="#555555"
                    :type="EButtonType.SUBMIT"
                    :btn-style="EButtonStyle.CTA"
                    :show-dialog="showDialog"
                    :dialog-content="dialogContent"
                    :full-width="true"
                    @close-dialog="onDialogWindowClosed"
                  />

                  <p class="text-[14px] leading-5">
                    <Trans
                      keypath="forms.termsDescription"
                      tag="label"
                      for="forms.privacy"
                    >
                      <a
                        href="/agb"
                        target="_blank"
                        class="font-bold text-vdv-base"
                      >
                        {{ t('forms.agb') }}
                      </a>
                      <a
                        href="/datenschutz"
                        target="_blank"
                        class="font-bold text-vdv-base"
                      >
                        {{ t('forms.privacy') }}
                      </a>
                    </Trans>
                  </p>
                </FormKit>
              </div>
            </div>
          </ContainerBasic>
        </div>
      </div>
    </ClientOnly>
  </ContainerBasic>
</template>

<script setup lang="ts">
import { reset } from '@formkit/core';
import { useResizeObserver } from '@vueuse/core';
import { EButtonStyle, EButtonType } from '@/@types/basic-button';
import ContainerBasic from '@/components/components/container-basic.vue';
import { Headline, WYSIWYGContentRaw } from '~/complib';
import ButtonBasic from '~~/src/components/components/core/basic-button/vdv/basic-button.vue';
import { mq_breakpointIsMobile } from '@/injectionSymbols';
import type { MailchimpPaywallFormData } from './blockType';
import Trans from '@/components/Trans.vue';
import { FormKit } from '@formkit/vue';

const { t } = useTrans();
const isMobile = inject(mq_breakpointIsMobile);
const isDesktopView = useIsDesktopView();
const form = ref(null);
const bannerNode = ref(null);
const imageUrl = ref('');
const text = ref(null);

const showDialog = ref(false);
const dialogContent = ref({
  dialogHeadline: '',
  dialogText: '',
});
const dialogContentSuccess = {
  dialogHeadline: t('forms.dialogContent.successHeader'),
  dialogText: t('forms.dialogContent.successBody'),
};
const dialogContentError = {
  dialogHeadline: t('forms.dialogContent.errorHeader'),
  dialogText: '',
  dialogType: 'error',
};

const props = defineProps({
  blockData: {
    type: Object as PropType<MailchimpPaywallFormData>,
    required: true,
    default: {} as MailchimpPaywallFormData,
  },
});

onMounted(() => {
  imageUrl.value = props.blockData.image
    ? props.blockData.image[isMobile.value ? 'mobile' : 'desktop'].url
    : '';
});

useResizeObserver(bannerNode, () => {
  imageUrl.value = props.blockData.image
    ? props.blockData.image[isMobile.value ? 'mobile' : 'desktop'].url
    : '';

  if (!isDesktopView.value) {
    text.value.classList.add(
      'inline-flex',
      'items-baseline',
      'gap-2',
      'bottom-3',
    );
  } else {
    text.value.classList.remove(
      'inline-flex',
      'items-baseline',
      'gap-2',
      'bottom-3',
    );
  }
});

function onDialogWindowClosed() {
  showDialog.value = false;
  reset('paywallForm');
}

if (import.meta.client) {
  (window as any).mc = {
    validateResponse: function (response: any) {
      dialogContent.value = {
        dialogHeadline: '',
        dialogText: '',
      };
      if (response.result === 'success') {
        dialogContent.value = dialogContentSuccess;
        showDialog.value = true;
      } else {
        dialogContentError.dialogText =
          t('forms.dialogContent.errorBody') +
          '<br />' +
          '<span class="italic">' +
          response.msg +
          '</span>';
        dialogContent.value = dialogContentError;
        showDialog.value = true;
      }
    },
  };
}

function onSubmit() {
  (document.activeElement as HTMLElement)?.blur();
  let url = props.blockData.formAction;
  url = url.replace('/post?u=', '/post-json?u=');
  url +=
    '&' +
    new URLSearchParams(form.value.value).toString() +
    '&c=window.mc.validateResponse';

  // Create script with url and callback (if specified)
  const script = window.document.createElement('script');
  script.src = url;

  // Insert script tag into the DOM (append to <head>)
  const ref = window.document.getElementsByTagName('script')[0];
  ref.parentNode.insertBefore(script, ref);

  // After the script is loaded (and executed), remove it
  script.onload = function () {
    ref.parentNode.removeChild(script);
  };
}

function watchForm(n: any) {
  form.value = n;
}
</script>

<style scoped lang="postcss"></style>
